import { Component, forwardRef, Input } from '@angular/core';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormControl,
} from '@angular/forms';
import { FieldTeamLink } from 'src/shared/api';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	map,
	takeUntil,
	first,
	filter,
	distinctUntilChanged,
	skipWhile,
} from 'rxjs/operators';
import { displayFieldTeam, compareTeam } from 'src/shared/enums';
import { LookUpsService } from 'src/shared/lookups.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { touchControl } from 'src/shared/utilities/utility-functions';

@Component({
	selector: 'rpshared-field-team-link',
	templateUrl: './field-team-link.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldTeamLinkComponent),
			multi: true,
		},
	],
})
export class FieldTeamLinkComponent extends SelfDestruct
	implements ControlValueAccessor {
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}
	fieldTeam = new FormControl('') as FormControl;
	initialValue: FieldTeamLink;

	readonly fieldTeams$ = this.lookUpsService.fieldTeams;
	readonly displayFieldTeam = displayFieldTeam;
	readonly compareTeam = compareTeam;

	constructor(private readonly lookUpsService: LookUpsService) {
		super();
	}

	// model --> view
	writeValue(value: FieldTeamLink | number): void {
		if (typeof value === 'number') {
			this.lookUpsService.fieldTeams
				.pipe(
					first(),
					map((teams) => teams.find((t) => t.id === value))
				)
				.subscribe((team) => {
					this.initialValue = team;
					this.fieldTeam.setValue(team, { emitEvent: false });
				});
		} else if (value) {
			this.initialValue = value;
			this.fieldTeam.setValue(value, { emitEvent: false });
		} else {
			this.fieldTeam.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: FieldTeamLink | number) => void) {
		this.fieldTeam.valueChanges
			.pipe(takeUntil(this.destroyed))
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.fieldTeam)(fn);
	}

	onTouched: () => void = () => {};
}

@Component({
	selector: 'rpshared-field-team-id',
	templateUrl: './field-team-link.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FieldTeamIdComponent),
			multi: true,
		},
	],
})
export class FieldTeamIdComponent extends FieldTeamLinkComponent {
	registerOnChange(fn: (value: FieldTeamLink | number) => void) {
		this.fieldTeam.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				map((v) => (v ? v.id : null)),
				distinctUntilChanged(),
				skipWhile<number | null>((v) => !v && !this.initialValue),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
